import { observer } from "mobx-react-lite";

import { Accent } from "@/components/expression/utils/decorators/Accent";
import { Bar } from "@/components/expression/utils/decorators/Bar";
import { Boxed } from "@/components/expression/utils/decorators/Boxed";
import { Brace } from "@/components/expression/utils/decorators/Brace";
import { Breve } from "@/components/expression/utils/decorators/Breve";
import { Cancel } from "@/components/expression/utils/decorators/Cancel";
import { Check } from "@/components/expression/utils/decorators/Check";
import { Dot } from "@/components/expression/utils/decorators/Dot";
import { Hat } from "@/components/expression/utils/decorators/Hat";
import { Ring } from "@/components/expression/utils/decorators/Ring";
import { Tilde } from "@/components/expression/utils/decorators/Tilde";
import { Vector } from "@/components/expression/utils/decorators/Vector";
import { WideHat } from "@/components/expression/utils/decorators/WideHat";
import { WideTilde } from "@/components/expression/utils/decorators/WideTilde";

import type { DecoratorTypeValue } from "@/components/suggestions/providers/data/types";
import type { DecoratorNodeModel } from "@/models/nodes/DecoratorNodeModel";
import type { FunctionComponent, PropsWithChildren } from "react";

type Props = {
  node: DecoratorNodeModel;
  isSelected?: boolean;
};

type DecoratorComponents = {
  [T in DecoratorTypeValue]:
    | FunctionComponent<
        PropsWithChildren<{
          node: DecoratorNodeModel;
          isSelected?: boolean;
        }>
      >
    | undefined;
};

export const RENDERER_MAP: DecoratorComponents = {
  vector: Vector,
  overrightarrow: (props) => <Vector {...props} position="over" />,
  underrightarrow: (props) => <Vector {...props} position="under" />,
  overleftarrow: (props) => <Vector {...props} position="over" direction="left" />,
  underleftarrow: (props) => <Vector {...props} position="under" direction="left" />,
  overleftrightarrow: (props) => <Vector {...props} position="over" direction="both" />,
  underleftrightarrow: (props) => <Vector {...props} position="under" direction="both" />,
  bar: Bar,
  overline: Bar,
  underline: (props) => <Bar {...props} position="under" />,
  hat: Hat,
  check: Check,
  breve: Breve,
  ring: Ring,
  tilde: Tilde,
  dot: Dot,
  "double-dot": (props) => <Dot {...props} count={2} />,
  "triple-dot": (props) => <Dot {...props} count={3} />,
  "quadruple-dot": (props) => <Dot {...props} count={4} />,
  "acute-accent": (props) => <Accent {...props} />,
  "double-acute-accent": (props) => <Accent {...props} count={2} />,
  "grave-accent": (props) => <Accent {...props} direction="right" />,
  overbrace: (props) => <Brace {...props} position="over" />,
  underbrace: (props) => <Brace {...props} position="under" />,
  boxed: Boxed,
  cancel: Cancel,
  bcancel: (props) => <Cancel {...props} type="bcancel" />,
  xcancel: (props) => <Cancel {...props} type="xcancel" />,
  widehat: WideHat,
  widetilde: WideTilde,
};

export const DecoratorNode = observer(function DecoratorNode({ node, isSelected }: Props) {
  const Component = RENDERER_MAP[node.name];
  return Component ? <Component node={node} isSelected={isSelected} /> : null;
});
